export type ImportExecutionProcessingResult = {
  overall_state?: 'complete' | 'partially_failed_recoverable' | 'failed';
  invoices_created?: number;
  reservations_created?: number;
  skipped_rows_count?: number;
  duplicate_invoices_skipped_count?: number;
  failed_groups?: number;
  recoverable_groups?: number;
  unexplained_local_only_count?: number;
  invoices_created_ids?: string[];
  reservations_created_ids?: string[];
  skipped_rows?: unknown[];
  duplicate_invoices_skipped?: unknown[];
  errors?: unknown[];
};

export type ImportExecutionResponse = {
  invoices_created?: number;
  processing_result?: ImportExecutionProcessingResult | null;
};

export function isRecoverableImportExecutionFailure(error: any): boolean {
  const status = error?.response?.status;
  return error?.code === 'ECONNABORTED'
    || error?.code === 'ERR_NETWORK'
    || !error?.response
    || (Number.isFinite(status) && status >= 500);
}

export function getImportExecutionCounts(data?: ImportExecutionResponse | null) {
  const result = data?.processing_result || null;
  const invoicesCreated = Number.isFinite(data?.invoices_created)
    ? Number(data?.invoices_created)
    : Number.isFinite(result?.invoices_created)
      ? Number(result?.invoices_created)
      : Array.isArray(result?.invoices_created_ids)
        ? result!.invoices_created_ids!.length
        : 0;

  const duplicatesSkipped = Number.isFinite(result?.duplicate_invoices_skipped_count)
    ? Number(result?.duplicate_invoices_skipped_count)
    : Array.isArray(result?.duplicate_invoices_skipped)
      ? result!.duplicate_invoices_skipped!.length
      : 0;

  const rowsSkipped = Number.isFinite(result?.skipped_rows_count)
    ? Number(result?.skipped_rows_count)
    : Array.isArray(result?.skipped_rows)
      ? result!.skipped_rows!.length
      : 0;

  const failedGroups = Number.isFinite(result?.failed_groups)
    ? Number(result?.failed_groups)
    : Array.isArray(result?.errors)
      ? result!.errors!.length
      : 0;

  const reservationsCreated = Number.isFinite(result?.reservations_created)
    ? Number(result?.reservations_created)
    : Array.isArray(result?.reservations_created_ids)
      ? result!.reservations_created_ids!.length
      : 0;

  const recoverableGroups = Number.isFinite(result?.recoverable_groups)
    ? Number(result?.recoverable_groups)
    : 0;
  const unexplainedLocalOnly = Number.isFinite(
    result?.unexplained_local_only_count
  )
    ? Number(result?.unexplained_local_only_count)
    : 0;

  return {
    invoicesCreated,
    duplicatesSkipped,
    rowsSkipped,
    failedGroups,
    reservationsCreated,
    recoverableGroups,
    unexplainedLocalOnly,
    overallState:
      result?.overall_state ||
      (failedGroups > 0 ? 'partially_failed_recoverable' : 'complete'),
  };
}

export function formatImportExecutionToast(data?: ImportExecutionResponse | null): string {
  const counts = getImportExecutionCounts(data);
  const details: string[] = [];
  if (counts.duplicatesSkipped > 0) {
    details.push(`${counts.duplicatesSkipped} matched existing invoice(s)`);
  }
  if (counts.rowsSkipped > 0) {
    details.push(`${counts.rowsSkipped} excluded or invalid row(s)`);
  }
  if (counts.failedGroups > 0) {
    details.push(`${counts.failedGroups} failed`);
  }
  if (counts.unexplainedLocalOnly > 0) {
    details.push(
      `${counts.unexplainedLocalOnly} unexplained provider state(s)`
    );
  }

  const suffix = details.length > 0 ? ` (${details.join(', ')})` : '';
  const prefix = counts.overallState === 'complete' ? 'Created' : 'Processed';
  return `${prefix} ${counts.invoicesCreated} invoice(s)${suffix}`;
}
